import { Menu as MenuIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

interface ShellContextValue {
  openNav: () => void;
  closeNav: () => void;
  navOpen: boolean;
}
const ShellContext = React.createContext<ShellContextValue | null>(null);

export function useAppShell(): ShellContextValue | null {
  return React.useContext(ShellContext);
}

export interface AppShellProps {
  sidebar: React.ReactNode;
  topbar: React.ReactNode;
  children: React.ReactNode;
  /** Label for the navigation landmark. */
  navLabel?: string;
  className?: string;
}

/**
 * Viewport-filling application frame: fixed ~240px sidebar + 72px topbar on ≥1024px;
 * below that the sidebar becomes an accessible modal drawer opened from the topbar.
 */
export function AppShell({ sidebar, topbar, children, navLabel = 'Navegación principal', className }: AppShellProps) {
  const [navOpen, setNavOpen] = React.useState(false);
  const opener = React.useRef<HTMLElement | null>(null);
  const ctx = React.useMemo(
    () => ({
      openNav: () => {
        opener.current = document.activeElement as HTMLElement | null;
        setNavOpen(true);
      },
      closeNav: () => setNavOpen(false),
      navOpen,
    }),
    [navOpen],
  );
  return (
    <ShellContext.Provider value={ctx}>
      <div className={cn('min-h-dvh bg-surface-canvas text-text-primary', className)}>
        <a
          href="#cm-main"
          className="sr-only z-[70] rounded-control bg-surface-default px-4 py-2 text-body font-medium text-brand-primary shadow-overlay focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
        >
          Saltar al contenido
        </a>
        <nav aria-label={navLabel} className="fixed inset-y-0 left-0 z-30 hidden w-sidebar border-r border-border-subtle bg-surface-default lg:flex">
          {sidebar}
        </nav>
        <DialogPrimitive.Root open={navOpen} onOpenChange={setNavOpen}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-surface-overlay animate-fade-in lg:hidden" />
            <DialogPrimitive.Content
              onCloseAutoFocus={(e) => {
                const el = opener.current;
                opener.current = null;
                if (el?.isConnected) {
                  e.preventDefault();
                  el.focus();
                }
              }}
              className="fixed inset-y-0 left-0 z-50 flex w-[min(280px,85vw)] bg-surface-default shadow-overlay animate-slide-in-left lg:hidden">
              <DialogPrimitive.Title className="sr-only">{navLabel}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">Menú de secciones</DialogPrimitive.Description>
              <nav aria-label={navLabel} className="flex w-full" onClick={(e) => {
                if ((e.target as HTMLElement).closest('a[href]')) setNavOpen(false);
              }}>
                {sidebar}
              </nav>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
        <div className="flex min-h-dvh min-w-0 flex-col lg:pl-sidebar">
          {topbar}
          <main id="cm-main" tabIndex={-1} className="min-w-0 flex-1 px-4 py-6 outline-none md:px-7 md:py-7 xl:px-8">
            {children}
          </main>
        </div>
      </div>
    </ShellContext.Provider>
  );
}

export interface SidebarProps {
  brand: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export function Sidebar({ brand, children, footer }: SidebarProps) {
  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex h-topbar shrink-0 items-center px-5">{brand}</div>
      <div className="flex-1 overflow-y-auto px-3 py-2">
        <ul className="grid gap-1">{children}</ul>
      </div>
      {footer ? <div className="shrink-0 border-t border-border-subtle p-3">{footer}</div> : null}
    </div>
  );
}

export function SidebarSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <li className="mt-4 first:mt-0">
      <p className="px-3 pb-1 text-helper font-medium uppercase tracking-wide text-text-muted">{label}</p>
      <ul className="grid gap-1">{children}</ul>
    </li>
  );
}

export interface NavItemProps {
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  badge?: React.ReactNode;
  /** Render a custom link element (e.g. a router Link) — receives className and aria-current. */
  render?: (props: { className: string; 'aria-current'?: 'page'; children: React.ReactNode }) => React.ReactNode;
}

export function NavItem({ label, icon, active, href, onClick, badge, render }: NavItemProps) {
  const className = cn(
    'group flex h-10 w-full items-center gap-3 rounded-control px-3 text-body font-medium transition-colors duration-[var(--cm-motion-fast)]',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus [&_svg]:size-[18px] [&_svg]:shrink-0',
    active ? 'bg-brand-tint text-brand-hover [&_svg]:text-brand-primary' : 'text-text-secondary hover:bg-surface-subtle hover:text-text-primary',
  );
  const children = (
    <>
      {icon}
      <span className="flex-1 truncate text-left">{label}</span>
      {badge}
    </>
  );
  const current = active ? ('page' as const) : undefined;
  return (
    <li>
      {render ? (
        render({ className, 'aria-current': current, children })
      ) : href ? (
        <a href={href} onClick={onClick} className={className} aria-current={current}>
          {children}
        </a>
      ) : (
        <button type="button" onClick={onClick} className={className} aria-current={current}>
          {children}
        </button>
      )}
    </li>
  );
}

export interface TopbarProps {
  /** Left side content (e.g. tenant switcher or search). */
  start?: React.ReactNode;
  /** Right side actions (user menu, notifications). */
  end?: React.ReactNode;
  className?: string;
}

export function Topbar({ start, end, className }: TopbarProps) {
  const shell = useAppShell();
  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex h-topbar shrink-0 items-center gap-3 border-b border-border-subtle bg-surface-default px-4 md:px-7 xl:px-8',
        className,
      )}
    >
      {shell ? (
        <button
          type="button"
          onClick={shell.openNav}
          aria-label="Abrir menú"
          aria-expanded={shell.navOpen}
          className="-ml-1 inline-flex size-10 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus lg:hidden"
        >
          <MenuIcon aria-hidden className="size-5" />
        </button>
      ) : null}
      <div className="flex min-w-0 flex-1 items-center gap-3">{start}</div>
      {end ? <div className="flex shrink-0 items-center gap-2">{end}</div> : null}
    </header>
  );
}

export interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  /** Small row above the title (breadcrumb / back link). */
  eyebrow?: React.ReactNode;
  /** Badges next to the title (e.g. Demo). */
  badges?: React.ReactNode;
  className?: string;
}

export function PageHeader({ title, description, actions, eyebrow, badges, className }: PageHeaderProps) {
  return (
    <div className={cn('mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between', className)}>
      <div className="grid min-w-0 gap-1">
        {eyebrow ? <div className="text-helper text-text-secondary">{eyebrow}</div> : null}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-heading font-semibold tracking-[-0.01em] text-text-primary">{title}</h1>
          {badges}
        </div>
        {description ? <div className="max-w-3xl text-body text-text-secondary">{description}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
